import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { LEVELS, TOTAL_LESSONS } from '@notion-training/shared';
import type { LevelMeta } from '@notion-training/shared';
import { api } from '../lib/api.js';

type LessonsResponse = {
  levels: LevelMeta[];
  lessons: Array<{ slug: string; title: string; level: number; order: number; description: string }>;
};

type MeResponse = {
  user: { email: string; name: string } | null;
  progress?: { completedSlugs?: string[] };
};

export function Home() {
  const lessonsQuery = useQuery({
    queryKey: ['lessons'],
    queryFn: () => api.get<LessonsResponse>('/api/lessons'),
  });

  // Read the user's progress so the per-level cards can show the real count.
  // Fail-soft: anonymous / unauthed users just see 0/N — same as before.
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<MeResponse>('/api/me'),
  });
  const completedSlugs = new Set(meQuery.data?.progress?.completedSlugs ?? []);

  return (
    <div className="view">
      <div className="grid-bg" style={{ borderBottom: '1px solid var(--color-border)' }}>
        <div
          style={{
            maxWidth: 1240,
            margin: '0 auto',
            padding: '64px 32px 56px',
          }}
        >
          <div className="eyebrow" style={{ marginBottom: 22 }}>
            ▌ Vim · Internal Training · Q1 2026 Rollout
          </div>
          <h1 className="h-display">
            Notion. <span className="tk">at</span> Vim.<br />
            <span className="acc">From&nbsp;Zero.</span>
          </h1>
          <p style={{ marginTop: 22, color: 'var(--color-text-2)', maxWidth: 540, fontSize: 15, lineHeight: 1.7 }}>
            Learn to navigate the Wiki, find any team's space, build databases, run Notion AI, and ship like the people
            who already do.{' '}
            <span style={{ color: 'var(--color-text-3)' }}>
              Twelve levels. {TOTAL_LESSONS} lessons. No prior experience required.
            </span>
          </p>
          <div style={{ display: 'flex', gap: 10, marginTop: 30, flexWrap: 'wrap' }}>
            <Link className="btn btn-primary" to="/lesson/find-your-teams-page">
              ▸ Start the demo lesson
            </Link>
            <Link className="btn" to="/dashboard">
              View my dashboard
            </Link>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1240, margin: '0 auto', padding: '56px 32px 96px' }}>
        <div style={{ marginBottom: 28 }}>
          <div className="eyebrow" style={{ marginBottom: 8 }}>
            ▌ The Curriculum
          </div>
          <h2 style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-0.03em', margin: 0 }}>
            Twelve levels. Ship at your own pace.
          </h2>
          <p style={{ color: 'var(--color-text-3)', marginTop: 8, fontSize: 13.5 }}>
            {lessonsQuery.isLoading
              ? 'Loading lessons…'
              : 'Each level lists its first lessons — click a lesson to start. Most levels are still being authored.'}
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
          {LEVELS.map((level) => {
            const lessonsInLevel = lessonsQuery.data?.lessons.filter((l) => l.level === level.id) ?? [];
            const completedInLevel = lessonsInLevel.filter((l) => completedSlugs.has(l.slug)).length;
            return (
              <LevelCard
                key={level.id}
                level={level}
                lessons={lessonsInLevel}
                completedCount={completedInLevel}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function LevelCard({
  level,
  lessons,
  completedCount,
}: {
  level: LevelMeta;
  lessons: Array<{ slug: string; title: string }>;
  completedCount: number;
}) {
  const isAi = level.flavor === 'ai';
  const isCapstone = level.flavor === 'capstone';
  const total = level.expectedLessons;
  const pct = total > 0 ? Math.round((completedCount / total) * 100) : 0;

  return (
    <div
      className="card"
      style={{
        padding: 22,
        ...(isAi
          ? {
              borderColor: 'rgba(180,140,255,0.2)',
              background: 'linear-gradient(180deg, rgba(180,140,255,0.04), var(--color-bg-card))',
            }
          : isCapstone
            ? { borderColor: 'rgba(255,180,84,0.2)' }
            : {}),
      }}
    >
      <div style={{ fontSize: 26, lineHeight: 1, marginBottom: 14, display: 'inline-flex' }}>{level.emoji}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span className="chip" style={{ fontSize: 10, padding: '2px 6px' }}>
          Level {level.id}
        </span>
        {isAi && (
          <span
            className="chip"
            style={{
              fontSize: 10,
              padding: '2px 6px',
              background: 'rgba(180,140,255,0.10)',
              color: 'var(--color-purple)',
              borderColor: 'rgba(180,140,255,0.22)',
            }}
          >
            New
          </span>
        )}
      </div>
      <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 4px', letterSpacing: '-0.01em' }}>{level.title}</h3>
      <p style={{ color: 'var(--color-text-3)', fontSize: 12.5, margin: '0 0 14px', lineHeight: 1.55 }}>
        {level.description}
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div className="progress" style={{ flex: 1 }}>
          <div className="progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <span style={{ fontSize: 11, color: 'var(--color-text-3)' }}>
          {completedCount}/{total}
        </span>
      </div>
      {lessons.length > 0 && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {lessons.slice(0, 3).map((lesson) => (
            <Link
              key={lesson.slug}
              to={`/lesson/${lesson.slug}`}
              style={{
                fontSize: 12,
                color: 'var(--color-text-2)',
                padding: '6px 8px',
                borderRadius: 6,
                background: 'rgba(255,255,255,0.02)',
              }}
            >
              ▸ {lesson.title}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
